import { useState, useRef, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { Home, ChevronDown, Gift, Newspaper } from 'lucide-react'
import { MAIN_NAV } from '../../config/nav.config'
import { NEWS_CATEGORIES } from '@/modules/news/config/news.config'
import { useAdminArticlesStore } from '@/modules/admin/state/articlesStore'

export function MainNav() {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  const ref = useRef<HTMLUListElement>(null)
  const articles = useAdminArticlesStore((s) => s.articles) ?? []
  const recentArticles = articles
    .filter((a) => a.status === 'published')
    .slice(0, 5)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenIdx(null)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <nav className="flex-1 flex items-center">
      <ul ref={ref} className="flex items-center gap-1">
        {MAIN_NAV.map((item, idx) => {
          const isHome = idx === 0
          const isPromo = item.label === 'Khuyến mãi'
          const isNews = item.label === 'Tin tức'

          return (
            <li key={item.to} className="relative">
              {item.hasDropdown ? (
                <button
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                  className="inline-flex items-center gap-1.5 px-4 h-14 text-[15px] font-medium transition border-b-2 text-text border-transparent hover:text-brand-600"
                >
                  {isHome && <Home className="w-4 h-4" />}
                  {isNews && <Newspaper className="w-4 h-4 text-brand-600" />}
                  <span>{item.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${openIdx === idx ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <NavLink
                  to={item.to}
                  end={isHome}
                  className={({ isActive }) =>
                    [
                      'inline-flex items-center gap-1.5 px-4 h-14 text-[15px] font-medium transition border-b-2',
                      isActive ? 'text-brand-600 border-brand-600' : 'text-text border-transparent hover:text-brand-600',
                    ].join(' ')
                  }
                >
                  {isHome && <Home className="w-4 h-4" />}
                  {isPromo && <Gift className="w-4 h-4 text-brand-600" />}
                  <span>{item.label}</span>
                </NavLink>
              )}

              {/* Dropdown Tin tức */}
              {isNews && openIdx === idx && (
                <div className="absolute left-0 top-full mt-0 w-[480px] bg-surface border border-border rounded-lg shadow-hover z-40 overflow-hidden grid grid-cols-2">
                  <div className="border-r border-border p-3">
                    <div className="text-xs font-semibold text-muted uppercase mb-2 px-2">Chuyên mục</div>
                    {NEWS_CATEGORIES.filter((c) => c !== 'Tất cả').map((cat) => (
                      <Link
                        key={cat}
                        to={`/tin-tuc?category=${encodeURIComponent(cat)}`}
                        onClick={() => setOpenIdx(null)}
                        className="flex items-center gap-2 h-9 px-2 rounded-md text-sm text-text hover:bg-brand-50 hover:text-brand-600 transition"
                      >
                        <Newspaper className="w-3.5 h-3.5 opacity-60" />
                        {cat}
                      </Link>
                    ))}
                    <Link
                      to="/tin-tuc"
                      onClick={() => setOpenIdx(null)}
                      className="mt-2 block text-center h-9 pt-2 text-xs font-semibold text-brand-600 hover:underline"
                    >
                      Xem tất cả →
                    </Link>
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-semibold text-muted uppercase mb-2 px-1">Bài viết mới</div>
                    {recentArticles.length === 0 ? (
                      <div className="text-xs text-muted px-1 py-4 text-center">Chưa có bài viết</div>
                    ) : (
                      recentArticles.map((a) => (
                        <Link
                          key={a.id}
                          to={`/tin-tuc/${a.slug}`}
                          onClick={() => setOpenIdx(null)}
                          className="block py-2 px-1 hover:bg-slate-50 rounded transition"
                        >
                          <div className="text-xs font-medium text-text line-clamp-2 leading-snug">{a.title}</div>
                          <div className="text-[11px] text-muted mt-0.5">{a.date}</div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
